import { Button, Checkbox, Form, Input, Space, Switch, TimePicker } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import AddIcon from "../../../../../../assets/icons/AddIcon";
import InfoItem from "../../../../../../components/common/InfoItem";
import Loader from "../../../../../../components/ui/Loader";
import { dbTimeFormat, mailRegex } from "../../../../../../constants/constants";
import {
  useCreateNotificationSettingMutation,
  useGetNotificationSettingQuery,
} from "../../../../../../features/notification/notificationApi";
import useError from "../../../../../../hooks/useError";
import { toast } from "../../../../../../lib/toast";
import { isValidTLD } from "../../../../../../utils/utils";

const infoItemProps = {
  rootClassName: "grid grid-cols-3",
  titleClassName: "!w-full",
  className: "!col-span-2",
};

const days = [
  { label: "Saturday", value: "Saturday" },
  { label: "Sunday", value: "Sunday" },
  { label: "Monday", value: "Monday" },
  { label: "Tuesday", value: "Tuesday" },
  { label: "Wednesday", value: "Wednesday" },
  { label: "Thursday", value: "Thursday" },
  { label: "Friday", value: "Friday" },
];

export default function GeneralSettings() {
  const [form] = Form.useForm();
  const isNotification = Form.useWatch("enable_daily_summary_email", form);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [createNotificationSetting, { isLoading, isError, error, isSuccess }] =
    useCreateNotificationSettingMutation();
  const {
    isError: isGetError,
    error: getError,
    isLoading: isGetLoading,
    data: getData,
  } = useGetNotificationSettingQuery({});
  useError(isError, error, "Create notification fail");
  useError(isGetError, getError, "Get notification fail");

  const onFinish = (values: any) => {
    const newValues = {
      ...values,
      send_time: values?.send_time?.format(dbTimeFormat),
    };

    createNotificationSetting(newValues);
  };

  useEffect(() => {
    const data = getData?.data?.setting;

    form.setFieldsValue({
      enable_daily_summary_email: data?.enable_daily_summary_email,
      send_time: data?.send_time
        ? dayjs(data?.send_time, dbTimeFormat)
        : undefined,
      delivery_days: data?.delivery_days,
      recipients: data?.recipients,
    });
  }, [form, getData?.data?.setting]);

  useEffect(() => {
    if (isSuccess) {
      toast("success", "Notification setting added successfully");
    }
  }, [isSuccess]);

  return (
    <div className="bg-white p-6 rounded-md border border-extra-light">
      <h3 className="text-header text-xl font-semibold">General Settings</h3>

      {isGetLoading ? (
        <Loader />
      ) : (
        <Form
          form={form}
          initialValues={{ isNotification: false, recipients: [] }}
          onFinish={onFinish}
        >
          <div className="py-6">
            <InfoItem {...infoItemProps} title="Notification">
              <div className="flex items-center gap-2">
                <Form.Item
                  name="enable_daily_summary_email"
                  valuePropName="checked"
                  className="mb-0"
                >
                  <Switch />
                </Form.Item>
                {isNotification ? (
                  <span className="font-medium text-header">Activated</span>
                ) : (
                  <span className="font-medium text-header">Deactivated</span>
                )}
              </div>
            </InfoItem>

            <InfoItem {...infoItemProps} title="Schedule Time">
              <Form.Item
                name="send_time"
                className="mb-0"
                rules={[
                  {
                    required: true,
                    message: "Please enter schedule time",
                  },
                ]}
              >
                <TimePicker
                  className="w-full border-x-0 border-t-0 rounded-none"
                  placeholder="HH:MM"
                  format={"hh:mm A"}
                  style={{ width: 150 }}
                  // onChange={(e) => {
                  //   console.log(e.format(dbTimeFormat));
                  // }}
                />
              </Form.Item>
            </InfoItem>

            <InfoItem {...infoItemProps} title="Delivery Days">
              <Form.Item
                name="delivery_days"
                className="mb-0"
                rules={[
                  {
                    required: true,
                    message: "Please select days",
                  },
                ]}
              >
                <Checkbox.Group className="grid grid-cols-4 gap-x-1 gap-y-2 p-2.5">
                  {days.map((item) => (
                    <Checkbox
                      key={item.value}
                      value={item.value}
                      className="text-header !text-sm font-medium"
                    >
                      {item.label}
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              </Form.Item>
            </InfoItem>

            <InfoItem {...infoItemProps} title="Recipients">
              <Form.List name="recipients">
                {(fields, { add, remove }) => (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2 items-center">
                      {fields.map(({ key, name }) => {
                        const email = form.getFieldValue(["recipients", name]);
                        return (
                          <div
                            key={key}
                            className="flex items-center bg-status-dark-bg rounded px-2 py-1"
                          >
                            <span className="text-sm font-normal text-display-body">
                              {email}
                            </span>
                            <Button
                              type="link"
                              size="small"
                              onClick={() => remove(name)}
                              className="!text-body-text"
                            >
                              ✕
                            </Button>
                          </div>
                        );
                      })}
                    </div>

                    {showEmailInput ? (
                      <Form.Item
                        name="tempEmail"
                        className="mb-0"
                        validateFirst
                        rules={[
                          {
                            required: true,
                            message: "Please enter email",
                          },
                          {
                            validator: async (_: any, value: string) => {
                              if (!value) return Promise.resolve();

                              if (!mailRegex.test(value)) {
                                return Promise.reject(
                                  new Error("Please enter a valid email")
                                );
                              }

                              if (!isValidTLD(value)) {
                                return Promise.reject(
                                  new Error("Please enter a valid TLD")
                                );
                              }

                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input
                          autoFocus
                          size="small"
                          placeholder="Enter email"
                          onPressEnter={async (e) => {
                            e.preventDefault();

                            const value = (
                              e.target as HTMLInputElement
                            ).value?.trim();

                            if (!value) return;

                            try {
                              await form.validateFields(["tempEmail"]);
                              add(value);
                              form.resetFields(["tempEmail"]);
                              setShowEmailInput(false);
                            } catch (err) {
                              // validation failed
                            }
                          }}
                          onBlur={async () => {
                            const value = form.getFieldValue("tempEmail");
                            if (!value?.trim()) {
                              setShowEmailInput(false);
                              return;
                            }
                            try {
                              await form.validateFields(["tempEmail"]);
                              add(value.trim());
                              form.resetFields(["tempEmail"]);
                              setShowEmailInput(false);
                            } catch {
                              // validation failed
                            }
                          }}
                          style={{ width: 200 }}
                        />
                      </Form.Item>
                    ) : (
                      <Button
                        type="link"
                        icon={<AddIcon />}
                        className="!text-primary !border-none"
                        onClick={() => setShowEmailInput(true)}
                      >
                        Add Email
                      </Button>
                    )}
                  </div>
                )}
              </Form.List>
            </InfoItem>
          </div>

          <Space className="w-full sticky bottom-0 bg-white" size={12}>
            <Button
              htmlType="submit"
              className="modal-button"
              type="primary"
              size="middle"
              // onClick={handleSubmit}
              loading={isLoading}
            >
              Save
            </Button>
          </Space>
        </Form>
      )}
    </div>
  );
}
